import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxDefaultOptions, MatCheckbox } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeedbackService } from '../../../../../core/feedback.service';
import { ApiResponse } from '../../../../../models/api-response';
import { Location } from '../../../../../models/locations-models/location';
import { GbpLocationGroup } from '../../../../../models/locations-models/gbp-location-group';
import { ApiService } from '../../../../../core/api-service';
import { HttpErrorResponse } from '@angular/common/http';
import { BulkEditLocationGrpsResponse } from '../../../../../models/locations-models/bulk-edit-location-grps-response';
import { Store } from '@ngrx/store';
import { LocationsRefreshActions } from '../../state/location-list-actions';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

enum groupStatesEnum {
  CHECKED = 'CHECKED', // No locations belonging to this business group
  UNCHECKED = 'UNCHECKED', // All selected locations belong to this business group
  INDETERMINATE = 'INDETERMINATE', // Some selected locations belong to this business group, but not all
  USER_CHECKED = 'USER_CHECKED', // All selected locations will be added to this business group
  USER_UNCHECKED = 'USER_UNCHECKED' // All selected locations will be removed from this business group
}

export interface BatchModifyLocationGrpsPopupData {
  locations: Location[];
  gbpGroups: GbpLocationGroup[];
  accountId: number;
}

export interface BatchModifyLocationGrpsPayload {
  locationIds: number[],
  groupsToBeAdded: string[],
  groupsToBeRemoved: string[]
}

@Component({
    selector: 'app-batch-modify-location-grps-popup',
    templateUrl: './batch-modify-location-grps-popup.component.html',
    styleUrls: ['./batch-modify-location-grps-popup.component.scss'],
    providers: [
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions, // let locationGrpsState
            // be the single source of truth for checkcircle state
        },
    ],
    standalone: true,
    imports: [
        MatDialogTitle,
        MatIcon,
        MatDialogContent,
        MatCheckbox,
        MatDialogActions,
        MatButton,
        MatProgressSpinner,
        TranslateModule,
    ],
})

export class BatchModifyLocationGrpsPopupComponent implements OnInit, OnDestroy {

  loading = false;
  locationGrpsState: {
    [gmbAccountId: string]:
        {
          accountName: string,
          originalState: groupStatesEnum,
          newState: groupStatesEnum
        }
  } = {};
  payload: BatchModifyLocationGrpsPayload;

  groupStatesEnum = groupStatesEnum;
  private componentDestroyed: Subject<void> = new Subject<void>();

  constructor(public dialogRef: MatDialogRef<BatchModifyLocationGrpsPopupComponent>,
              @Inject(MAT_DIALOG_DATA) public data: BatchModifyLocationGrpsPopupData,
              private apiService: ApiService,
              private feedbackService: FeedbackService,
              private store: Store) {
  }

  ngOnInit(): void {
    this.initLocationGrpsState();
    this.generatePayload();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Initiate the checkcircle/location grps state-keeping object
   */
  initLocationGrpsState() {
    this.data.gbpGroups.forEach(grp => {
      this.locationGrpsState[grp.gmbAccountId] = {
        accountName: grp.accountName,
        originalState: this.determineOriginalState(grp.gmbAccountId),
        newState: this.determineOriginalState(grp.gmbAccountId),
      };
    });
  }

  /**
   * Determines initial checkcircle state
   * @param gmbAccountId
   */
  private determineOriginalState(gmbAccountId): groupStatesEnum {
    if (this.data.locations.every(location => location.gmbAccountIds.includes(gmbAccountId))) {
      return groupStatesEnum.CHECKED;
    } else if (this.data.locations.some(location => location.gmbAccountIds.includes(gmbAccountId))) {
      return groupStatesEnum.INDETERMINATE;
    } else {
      return groupStatesEnum.UNCHECKED;
    }
  }

  /**
   * Determines new checkcircle state and updates payload for the BE call
   * Run on every user-select action
   * @param gmbAccountId
   */
  determineNewStateAndGeneratePayload(gmbAccountId) {
    const currentState = this.locationGrpsState[gmbAccountId].newState;
    const originalState = this.locationGrpsState[gmbAccountId].originalState;

    switch (currentState) {
      case groupStatesEnum.UNCHECKED:
        this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.USER_CHECKED;
        break;
      case groupStatesEnum.CHECKED:
        this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.USER_UNCHECKED;
        break;
      case groupStatesEnum.INDETERMINATE:
        this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.USER_CHECKED;
        break;
      case groupStatesEnum.USER_CHECKED:
        if (originalState === groupStatesEnum.UNCHECKED) {
          this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.UNCHECKED;
        } else {
          this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.USER_UNCHECKED;
        }
        break;
      case groupStatesEnum.USER_UNCHECKED:
        if (originalState === groupStatesEnum.CHECKED) {
          this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.CHECKED;
        } else {
          this.locationGrpsState[gmbAccountId].newState = groupStatesEnum.INDETERMINATE;
        }
        break;
    }

    this.generatePayload();
  }

  /**
   * Generates payload for the BE call
   */
  private generatePayload() {
    this.payload = {
      locationIds: this.data.locations.map(location => location.id),
      groupsToBeAdded: [],
      groupsToBeRemoved: [],
    };

    Object.keys(this.locationGrpsState).forEach(gmbAccountId => {
      if (this.locationGrpsState[gmbAccountId].newState !== this.locationGrpsState[gmbAccountId].originalState) {
        switch (this.locationGrpsState[gmbAccountId].newState) {
          case groupStatesEnum.USER_CHECKED:
            this.payload.groupsToBeAdded.push(gmbAccountId);
            break;
          case groupStatesEnum.USER_UNCHECKED:
            this.payload.groupsToBeRemoved.push(gmbAccountId);
            break;
        }
      }
    });
  }

  /**
   * Determines whether transfer operations selected by the user are possible
   * If some locations could be left without a grp, don't allow transfer!
   */
  isTransferPossible(): boolean {
    // if some locations could be left without a grp, don't allow transfer
    if (this.areLocationsWithoutGrps()) {
      return false;
      // check if there is payload to be sent
    } else return !!(this.payload?.groupsToBeRemoved?.length || this.payload?.groupsToBeAdded?.length);
  }

  /**
   * Determines whether some locations might not be part of any gmb group after the transfer
   * Used to show a warning banner
   */
  areLocationsWithoutGrps() {
    if (!this.payload?.groupsToBeRemoved?.length) {
      return false; // early return
    }

    // go through all locations ...
    return this.data.locations
        // ... and find at least 1 location where ...
        .some((location) => location.gmbAccountIds
                // ... if every location grp is removed ...
                .every((accountId) => this.payload.groupsToBeRemoved
                    // ... that is included in the 'groupsToBeRemoved' array ...
                    .includes(accountId)) &&
            // ... would mean that the location will not belong to any location grp afterwards
            location.gmbAccountIds.length - this.payload.groupsToBeRemoved.length === 0 &&
            // if we also aren't adding any new grp
            !this.payload.groupsToBeAdded.length);
  }

  /**
   * Requests add/remove from/to group operations for selected locations
   */
  transfer() {
    this.loading = true;
    this.apiService.bulkEditLocationGroups(this.data.accountId, this.payload)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe({
          next: (res) => {
            if (res.result?.failedLocationErrorMessages?.length) {
              // some operations succeeded - close the popup and refresh
              this.generateAdditionalInfoAndShowWarning(res);
            } else {
              this.feedbackService.showSuccessSnackbar({
                data: { userFriendlyMessage: 'Success!' },
                isAddressListSnackbar: true,
                duration: 3000,
              });
            }

            if (res.result?.successfulLocations?.length) {
              this.store.dispatch(LocationsRefreshActions.patchLocations({locations: res.result.successfulLocations}));
            }
            this.loading = false;
            this.dialogRef.close(true);
          }, error: (err: HttpErrorResponse) => {
            // client/server error - do not close the popup, retrying might help
            this.loading = false;
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: 'There was an error during the operation',
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : [err.message],
              },
              isAddressListSnackbar: true,
            }, err?.status);
          },
        });
  }

  /**
   * Generates additional information string from API response and shows an alert snackbar
   * @param response
   */
  private generateAdditionalInfoAndShowWarning(response: ApiResponse<BulkEditLocationGrpsResponse>) {
    const data = {
      userFriendlyMessage: 'Operation failed for some locations',
          detailedInfoFromBE: response.result?.failedLocationErrorMessages
          ?.map(fail => `Store Code: ${fail.storeCode}\nOperation: ${fail.operation}\nError Message: ${fail.errorMessage}`),
    };

    if (response.result?.successfulLocations?.length) {
      this.feedbackService.showWarningSnackbar({ data });
    } else {
      data.userFriendlyMessage = 'Operation failed for all locations';
      this.feedbackService.showAlertSnackbar({ data }, 0);
    }

  }


}
